// React and Hooks
import { useApolloClient, useMutation } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Actions
import {
  actionSetOperatorIsLoggingIn,
  actionSetRecentOperatorRuns,
} from 'lib/actions';
// Selectors
import { getOperatorIsLoggingIn } from 'lib/selectors/getOperatorIsLoggingIn';
// Utils
import { START_OPERATOR } from 'lib/api/mutations';
import { CHECK_OPEN_LABOR_TICKET_FOR_JOB } from 'lib/api/queries';
import { getIsLaborTicketEnabled } from 'lib/selectors/getIsLaborTicketEnabled';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getLoadingCurrentWorkOrderOp } from 'lib/selectors/getLoadingCurrentWorkOrderOp';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getLaborTicketMachineWhere } from 'lib/selectors/getLaborTicketWhereForMachine';
import { getOperators } from 'lib/selectors/getOperators';
import { getLatestActivityMode } from 'lib/selectors/getLatestActivityMode';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import useStartOperatorWithLaborTicket from './useStartOperatorWithLaborTicket';

// The ticket fields that identify a job: the labor ticket's foreign key to its
// work order operation (MATCH FULL), which useStartOperatorWithLaborTicket
// copies from the operation onto the ticket it creates. A ticket for the
// operation carries exactly the operation's values, so they match exactly.
const JOB_FIELDS = ['workOrderId', 'sequenceNumber', 'lot', 'split', 'sub'];

/**
 * Where clause for an open ticket of this operator on this machine for the
 * given job and activity type (a setup ticket doesn't stand in for production).
 * @param {object} machineWhere
 * @param {string} personId
 * @param {object} workOrderOp
 * @param {string|null} type - The ticket type sign-in would create
 */
const whereOpenTicketForOperatorJob = (
  machineWhere,
  personId,
  workOrderOp,
  type
) => {
  // Hasura can't _eq null; a null key column matches as null.
  const jobWhere = JOB_FIELDS.map((field) => {
    const value = workOrderOp[field];
    return value == null
      ? { [field]: { _is_null: true } }
      : { [field]: { _eq: value } };
  });

  return {
    _and: [
      machineWhere,
      { personId: { _eq: personId } },
      { clockOut: { _is_null: true } },
      { state: { _neq: 'CLOSED' } },
      // Ticket types vary in case across ERPs ('production' / 'Production').
      ...(type != null ? [{ type: { _ilike: type } }] : []),
      ...jobWhere,
    ],
  };
};

/**
 * Whether the latest state still has the same current job, activity type and
 * paused state as when the sign-in decision was made.
 * @param {object} latest - startOperatorStateRef.current
 * @param {object} workOrderOp
 * @param {string|null} type
 * @param {boolean} paused
 */
const isSameJob = (latest, workOrderOp, type, paused) => {
  const latestOp = latest.currentWorkOrderOp;
  if (!latestOp || latest.isPaused !== paused) {
    return false;
  }
  if (latest.latestActivityMode !== type) {
    return false;
  }
  return JOB_FIELDS.every((field) => {
    return latestOp[field] === workOrderOp[field];
  });
};

// Components

/**
 * Hook to manage operator login process using GraphQL mutation
 * @returns {Function} A function that starts an operator session:
 *   - Returns null if another operator is currently logging in
 *   - Otherwise returns a Promise resolving to the mutation result
 *
 * @example
 * const startOperator = useStartOperator();
 * const result = await startOperator({
 *   variables: {
 *     input: {
 *       machineId: '123',
 *       operatorId: '456',
 *       startAt: '2023-01-01T00:00:00Z'
 *     }
 *   }
 * });
 */
const useStartOperator = () => {
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const isLoggingIn = useSelector(getOperatorIsLoggingIn);
  const laborTicketEnabled = useSelector(getIsLaborTicketEnabled);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const loadingCurrentWorkOrderOp = useSelector(getLoadingCurrentWorkOrderOp);
  const isPaused = useSelector(getIsPaused);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const machineWhere = useSelector(getLaborTicketMachineWhere);
  const operators = useSelector(getOperators);
  const latestActivityMode = useSelector(getLatestActivityMode);
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);

  const startOperatorWithLaborTicket = useStartOperatorWithLaborTicket();

  const [startOperator, { loading }] = useMutation(START_OPERATOR, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ startOperatorResponse }) => {
      if (startOperatorResponse) {
        const { machine, operator, ...operatorRun } = startOperatorResponse;
        const mappedPayload = {
          operatorRuns: [
            {
              ...operatorRun,
              machineId: machine?.id,
              operatorId: operator?.id,
            },
          ],
        };
        dispatch(actionSetRecentOperatorRuns(mappedPayload));
      }
    },
  });

  useEffect(() => {
    dispatch(actionSetOperatorIsLoggingIn(loading));
    return () => {
      dispatch(actionSetOperatorIsLoggingIn(false));
    };
  }, [dispatch, loading]);

  const startOperatorStateRef = useRef({});
  // Set while the open-ticket check is awaited, before any mutation is loading.
  const checkingOpenTicketRef = useRef(false);
  startOperatorStateRef.current = {
    isLoggingIn,
    loading,
    laborTicketEnabled,
    loadingCurrentWorkOrderOp,
    currentWorkOrderOp,
    isPaused,
    isApmERPJobFlow,
    machineWhere,
    operators,
    latestActivityMode,
    natsFlowEnabled,
    // Latest render's create: it builds the ticket from that render's job.
    startOperatorWithLaborTicket,
  };

  /**
   * Whether the signing-in operator already has an open ticket for the job on
   * this machine. Open tickets aren't shown while nobody is signed in, so this
   * asks the server rather than the current ticket.
   */
  const hasOpenTicketForJob = async ({
    operatorId,
    workOrderOp,
    machineWhere: ticketMachineWhere,
    operators: operatorList,
    type,
  }) => {
    const personId = operatorList.find((op) => {
      return op.id === operatorId;
    })?.erpId;
    if (personId == null) {
      return false;
    }
    // Without a work order there is no job to match; create as before.
    if (workOrderOp?.workOrderId == null) {
      return false;
    }

    try {
      const { data } = await apolloClient.query({
        query: CHECK_OPEN_LABOR_TICKET_FOR_JOB,
        fetchPolicy: 'network-only',
        variables: {
          where: whereOpenTicketForOperatorJob(
            ticketMachineWhere,
            personId,
            workOrderOp,
            type
          ),
        },
      });
      return (data?.erpLaborTickets?.length || 0) > 0;
    } catch {
      // Can't tell: create the ticket as before rather than block sign-in.
      return false;
    }
  };

  /**
   * @param {Object} args - The mutation arguments
   * @param {Object} args.variables - The variables for the START_OPERATOR mutation
   * @param {Object} args.variables.input - Input object for starting an operator
   * @param {string} args.variables.input.machineId - The ID of the machine
   * @param {string} args.variables.input.operatorId - The ID of the operator
   * @param {boolean} [args.variables.input.pinToPreviousEvent] - Optional flag to pin to previous event
   * @param {string} [args.variables.input.startAt] - Optional ISO timestamp for start time
   * @returns {Promise<{data?: {startOperatorResponse?: Object}, errors?: Array<import('@apollo/client').ApolloError>}>}
   */
  const signIn = async (startOperatorRun, isRecheck = false) => {
    const {
      isLoggingIn: loggingIn,
      loading: startLoading,
      laborTicketEnabled: laborTicketsOn,
      loadingCurrentWorkOrderOp: workOrderOpLoading,
      currentWorkOrderOp: workOrderOp,
      isPaused: paused,
      isApmERPJobFlow: apmErpFlow,
      machineWhere: ticketMachineWhere,
      operators: operatorList,
      latestActivityMode: activityMode,
      natsFlowEnabled: natsFlow,
      startOperatorWithLaborTicket: createWithLaborTicket,
    } = startOperatorStateRef.current;

    if (loggingIn || startLoading || checkingOpenTicketRef.current) {
      return null;
    }

    // If labor tickets are enabled, wait for currentWorkOrderOp to finish loading
    // to avoid race condition where we call legacy startOperator instead of startOperatorWithLaborTicket
    if (laborTicketsOn && workOrderOpLoading && !apmErpFlow) {
      return null;
    }

    // APM ERP: program events populate currentWorkOrderOp for UI only — sign-in must not
    // create/open a labor ticket; operators confirm events separately.
    if (laborTicketsOn && workOrderOp && !paused && !apmErpFlow) {
      // Never open a second ticket for the same job, operator and machine: if
      // theirs is already open, just start their run against it.
      // NATS labor flow only: there the operator's open ticket is hidden while
      // nobody is signed in, so sign-in can't see it to avoid a duplicate.
      if (!natsFlow) {
        return createWithLaborTicket(startOperatorRun);
      }

      checkingOpenTicketRef.current = true;
      let alreadyOpen;
      try {
        alreadyOpen = await hasOpenTicketForJob({
          operatorId: startOperatorRun.operatorId,
          workOrderOp,
          machineWhere: ticketMachineWhere,
          operators: operatorList,
          type: activityMode,
        });
      } finally {
        checkingOpenTicketRef.current = false;
      }

      // The current job can change while the check is in flight (the ticket
      // subscription switches or clears it): decide again on the latest state
      // rather than create a ticket for a job that is no longer current.
      const latest = startOperatorStateRef.current;
      // The machine shown changed mid-check: the run (machineId captured at sign-in)
      // and a ticket (machineRef read now) would target different machines. Abort;
      // the operator signs in again on the machine now shown.
      if (latest.machineWhere !== ticketMachineWhere) {
        return null;
      }
      if (!isSameJob(latest, workOrderOp, activityMode, paused)) {
        if (!isRecheck) {
          return signIn(startOperatorRun, true);
        }
        // Changed again during the retry: the latest job is unchecked. Abort
        // like a machine change; the operator signs in again.
        return null;
      }

      if (!alreadyOpen) {
        return latest.startOperatorWithLaborTicket(startOperatorRun);
      }
    }

    return startOperator({
      variables: {
        input: startOperatorRun,
      },
    });
  };

  return (startOperatorRun) => {
    return signIn(startOperatorRun);
  };
};

export default useStartOperator;
